import {
  getQuestions,
  createCategory,
  updateCategory,
  deleteCategory,
  getUsers,
  deleteUser
} from '@/api/admin'
import request from '@/utils/request'

// 分类处理函数
const processCategories = (categories) => {
  // 按创建时间排序，越新的越靠后
  const sortedCategories = [...categories].sort((a, b) => {
    const timeA = new Date(a.createdAt).getTime()
    const timeB = new Date(b.createdAt).getTime()
    return timeA - timeB
  })

  return sortedCategories.map(cat => ({
    _id: cat._id,
    name: cat.name,
    stage: cat.stage || 'primary',
    parent_id: cat.parent_id || null,
    level: cat.level || 1,
    question_count: cat.question_count || 0,
    children: cat.children ? processCategories(cat.children) : undefined
  }))
}

const CATEGORY_CACHE_TTL_MS = 5 * 60 * 1000
let lastCategoriesFetchAt = 0
let inflightCategoriesPromise = null

export default {
  namespaced: true,

  state: {
    questions: [],
    categories: [],
    users: [],
    loading: false,
    total: 0,
    totalPages: 0,
    userTotal: 0,
    currentFilter: {
      page: 1,
      pageSize: 10,
      search: '',
      category_id: null
    }
  },

  mutations: {
    SET_QUESTIONS(state, questions) {
      state.questions = questions
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_USERS(state, users) {
      state.users = users
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_TOTAL(state, total) {
      state.total = total
    },
    SET_USER_TOTAL(state, total) {
      state.userTotal = total
    },
    SET_CURRENT_FILTER(state, filter) {
      state.currentFilter = filter
    },
    SET_TOTAL_PAGES(state, totalPages) {
      state.totalPages = totalPages
    }
  },

  getters: {
    categoryOptions: state => {
      return processCategories(state.categories)
    }
  },

  actions: {
    // 题目管理
    async fetchQuestions({ commit }, { page, pageSize, ...filterParams }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_CURRENT_FILTER', { page, pageSize, ...filterParams })
        
        const response = await getQuestions({ page, pageSize, ...filterParams })
        if (response.status === 'success') {
          commit('SET_QUESTIONS', response.data.questions)
          commit('SET_TOTAL', response.data.pagination.total_items)
          commit('SET_TOTAL_PAGES', response.data.pagination.total_pages)
        }
      } catch (error) {
        console.error('获取题目列表失败:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createQuestion(_, data) {
      try {
        const response = await request({
          url: '/questions',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          data
        })

        console.log('创建题目响应:', response)

        if (response.status === 'success') {
          return response.data
        }
        throw new Error(response.message || '创建失败')
      } catch (error) {
        console.error('创建题目失败:', error)
        throw error
      }
    },

    async updateQuestion(_, { data }) {
      try {
        const response = await request({
          url: `/questions/${data.question_id}`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          data
        })

        console.log('更新题目响应:', response)

        if (response.status === 'success') {
          return response.data
        }
        throw new Error(response.message || '更新失败')
      } catch (error) {
        console.error('更新题目失败:', error)
        throw error
      }
    },

    async deleteQuestion({ dispatch }, question_id) {
      try {
        const response = await request({
          url: `/questions/${question_id}`,
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
        if (response.status === 'success') {
          console.log('删除题目成功')
          dispatch('fetchQuestions', { page: 1, pageSize: 10 })
        } else {
          throw new Error(response.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        throw error
      }
    },

    // 分类管理
    async fetchCategories({ commit, state }, options = {}) {
      const { force = false } = options
      const now = Date.now()
      const hasCachedCategories = Array.isArray(state.categories) && state.categories.length > 0
      if (!force && hasCachedCategories && now - lastCategoriesFetchAt < CATEGORY_CACHE_TTL_MS) {
        return state.categories
      }
      if (!force && inflightCategoriesPromise) {
        return inflightCategoriesPromise
      }
      try {
        inflightCategoriesPromise = (async () => {
          commit('SET_LOADING', true)
          const response = await request({
            url: '/categories',
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })

          if (response.status === 'success') {
            const processedCategories = processCategories(response.data.categories)
            commit('SET_CATEGORIES', processedCategories)
            lastCategoriesFetchAt = Date.now()
            console.log('获取分类列表成功')
            return processedCategories
          }
          return state.categories
        })()
        return await inflightCategoriesPromise
      } catch (error) {
        console.error('获取分类列表失败:', error)
        throw error
      } finally {
        inflightCategoriesPromise = null
        commit('SET_LOADING', false)
      }
    },

    async createCategory({ dispatch, state }, { name, parentId, description, stage }) {
      try {
        // 递归查找分类及其子分类
        const findCategory = (categories, id) => {
          for (const cat of categories) {
            if (cat._id === id) return cat
            if (cat.children) {
              const found = findCategory(cat.children, id)
              if (found) return found
            }
          }
          return null
        }

        const parentCategory = parentId ? findCategory(state.categories, parentId) : null
        const level = parentCategory ? parentCategory.level + 1 : 1

        const response = await createCategory({
          name,
          parent_id: parentId,
          description,
          stage,
          level
        })
        if (response.status === 'success') {
          await dispatch('fetchCategories', { force: true })
        }
        return response
      } catch (error) {
        console.error('创建分类失败:', error)
        throw error
      }
    },

    async updateCategory({ dispatch }, { id, data }) {
      try {
        await updateCategory(id, data)
        dispatch('fetchCategories', { force: true })
        console.log('更新分类成功')
      } catch (error) {
        console.error('更新分类失败:', error)
        throw error
      }
    },

    async deleteCategory({ dispatch }, id) {
      try {
        await deleteCategory(id)
        dispatch('fetchCategories', { force: true })
        console.log('删除分类成功')
      } catch (error) {
        console.error('删除分类失败:', error)
        throw error
      }
    },

    // 用户管理
    async fetchUsers({ commit }) {
      try {
        commit('SET_LOADING', true)
        const response = await getUsers()
        if (response.status === 'success') {
          commit('SET_USERS', response.data.users)
          commit(
            'SET_USER_TOTAL',
            typeof response.data.total === 'number'
              ? response.data.total
              : response.data.users?.length ?? 0
          )
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createUser({ dispatch }, data) {
      try {
        const response = await request({
          url: '/users',
          method: 'POST',
          data
        })
        if (response.status === 'success') {
          await dispatch('fetchUsers')
          return response.data
        }
        throw new Error(response.message || '创建用户失败')
      } catch (error) {
        console.error('创建用户失败:', error)
        throw error
      }
    },

    async updateUser({ dispatch }, { id, data }) {
      try {
        const response = await request({
          url: `/users/${id}`,
          method: 'PATCH',
          data
        })
        if (response.status === 'success') {
          console.log('更新用户成功')
          await dispatch('fetchUsers')
        } else {
          throw new Error(response.message || '更新失败')
        }
      } catch (error) {
        console.error('更新失败:', error)
        throw error
      }
    },

    async deleteUser({ dispatch }, id) {
      try {
        const response = await deleteUser(id)
        if (response.status === 'success') {
          console.log('删除用户成功')
          await dispatch('fetchUsers')
        } else {
          throw new Error(response.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        throw error
      }
    }
  }
}